import React from "react";
import { Alert, AlertTitle, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useAppContext } from "../AppContext";
import axios from "axios";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { domainDetails } from "../domainDetails";

function UserVerification({ message, display }) {
    const theme = useTheme();
    const subdomain = window.location.host.split('.')[0];
    const hasCustomColors = !!domainDetails[subdomain]?.colors;

    const brandAlertSx = hasCustomColors ? {
        bgcolor: alpha(theme.palette.primary.main, 0.08),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        '& .MuiAlert-icon': { color: theme.palette.primary.main },
    } : {};

    const { values, setValues } = useAppContext();
    const [open, setOpen] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [showResendOtpButton, setShowResendOtpButton] = React.useState(false);

    const { handleSubmit, setError, register, formState: { errors }, clearErrors, resetField } = useForm({
        defaultValues: { otp: '' },
        shouldUnregister: true
    });

    const verifyOtp = async ({ otp }) => {

        try {

            clearErrors('otp');

            const payload = { awbNumber: values.orderDetails.waybill, otp }
            const response = await axios.post(`${window.REACT_APP_SERVICE_ENDPOINT}otp/validate`, payload)

            if (!response.data) {
                setError('otp', { type: 'custom', 'message': 'Incorrect OTP! please try again' })
                return;
            }

            setValues({ ...values, otpVerified: true });
            setOpen(false);

        } catch (err) {
            const statusCode = err?.response?.data?.subCode;
            if (statusCode === 8001) {
                setShowResendOtpButton(true);
            } else if (statusCode === 8000) {
                setShowResendOtpButton(false);
            }
            setError('otp', { type: 'custom', 'message': err?.response?.data?.message || 'Invalid OTP!' });
        }

    };

    const sendOtp = async () => {
        try {

            resetField('otp');
            setLoading(true);

            const request = { system: 'NDR', awbNumber: values.orderDetails.waybill };
            const response = await axios.post(`${window.REACT_APP_SERVICE_ENDPOINT}otp/generate`, request);
            if (response.data.status !== 'SUCCESS')
                throw new Error(response?.data?.responseObj || 'Error sending verification code');

            setOpen(true);
            setShowResendOtpButton(false);

        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (values && !values.otpVerified && (values.askForVerification || display)) {
        return (
            <>
                <Dialog open={open} onClose={() => setOpen(false)}>
                    <form onSubmit={handleSubmit(verifyOtp)}>
                        <DialogTitle>
                            OTP Verification
                        </DialogTitle>
                        <DialogContent>
                            <Alert severity="info" sx={{ mb: 2, ...brandAlertSx }}>
                                A 6-digit OTP has been sent to the mobile number associated with this shipment. Please enter the OTP to continue.
                            </Alert>
                            <TextField
                                placeholder="Please enter the OTP to continue.."
                                label="Please Enter OTP (6 Digit)"
                                fullWidth
                                autoFocus
                                error={!!errors.otp}
                                {...register("otp", { required: true, minLength: { value: 6, message: 'OTP must be 6 digits long' } })}
                            />
                            {errors.otp && errors.otp.message && (
                                <Alert
                                    severity="error"
                                    sx={{ mt: 1, ...brandAlertSx }}
                                    action={showResendOtpButton ? (
                                        <LoadingButton loading={loading} onClick={sendOtp} color={hasCustomColors ? "primary" : "error"}>
                                            Resend
                                        </LoadingButton>
                                    ) : null}
                                >
                                    {errors.otp.message}
                                </Alert>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button type="button" onClick={() => setOpen(false)} color={hasCustomColors ? "primary" : "error"}>Close</Button>
                            <Button disabled={showResendOtpButton} type="submit" variant="contained" disableElevation>Verify</Button>
                        </DialogActions>
                    </form>
                </Dialog>
                <Alert
                    severity={hasCustomColors ? "info" : "error"}
                    sx={brandAlertSx}
                    action={(
                        <LoadingButton loading={loading} onClick={sendOtp} color={hasCustomColors ? "primary" : "error"}>
                            Verify
                        </LoadingButton>
                    )}
                >
                    <AlertTitle>
                        User Verification
                    </AlertTitle>
                    {message || "We could not deliver your shipment in our last delivery attempt. Please contact the customer service."}
                </Alert>
            </>
        )
    }

    return null;
}

export default UserVerification;